import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Calculator, Lightbulb, ArrowRight } from "lucide-react";

const SITE_URL = "https://elvion-group.nl";

export const Route = createFileRoute("/led-calculator")({
  head: () => {
    const url = `${SITE_URL}/led-calculator`;
    const title =
      "LED Wattage Calculator — Bereken vermogen per ruimte | Elvion Group Nederland";
    const description =
      "Bereken eenvoudig hoeveel watt LED-verlichting je nodig hebt op basis van oppervlakte, plafondhoogte en type ruimte. Inclusief lumen-, lux- en armatuuradvies.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: LedCalculatorPage,
});

type RoomType = {
  key: string;
  label: string;
  lux: number;
  hint: string;
};

// Aanbevolen verlichtingssterkte (lux) per ruimtetype — NEN-EN 12464-1 richtlijnen
const ROOM_TYPES: RoomType[] = [
  { key: "woonkamer", label: "Woonkamer", lux: 150, hint: "Sfeervolle algemene verlichting" },
  { key: "slaapkamer", label: "Slaapkamer", lux: 100, hint: "Rustige basisverlichting" },
  { key: "keuken", label: "Keuken", lux: 300, hint: "Taakverlichting werkblad" },
  { key: "badkamer", label: "Badkamer", lux: 200, hint: "Helder, IP44+" },
  { key: "kantoor", label: "Kantoor", lux: 500, hint: "NEN-norm beeldschermwerk" },
  { key: "winkel", label: "Winkel / showroom", lux: 500, hint: "Productpresentatie" },
  { key: "werkplaats", label: "Werkplaats", lux: 750, hint: "Fijn handwerk / montage" },
  { key: "magazijn", label: "Magazijn / opslag", lux: 200, hint: "Loop- en pickzones" },
  { key: "garage", label: "Garage / parking", lux: 150, hint: "Veilig manoeuvreren" },
  { key: "buiten", label: "Buiten / terrein", lux: 50, hint: "Algemene terreinverlichting" },
];

// Hoogtefactor: hoe hoger het plafond, hoe meer lumen er nodig is (inverse square + verlies)
function heightFactor(h: number): number {
  if (h <= 2.6) return 1.0;
  if (h <= 3.5) return 1.2;
  if (h <= 5) return 1.5;
  if (h <= 7) return 2.0;
  if (h <= 10) return 2.6;
  return 3.2;
}

// LED-efficiency in lm/W per kwaliteitsniveau
const EFFICACY = {
  standaard: 90, // gemiddelde A-merk LED-lamp
  hoog: 130, // high-efficiency LED-paneel / TL-vervanger
  premium: 170, // industriële high-bay armatuur
} as const;
type EfficacyKey = keyof typeof EFFICACY;

const ARMATUUR_LUMEN = {
  woonkamer: 800,
  slaapkamer: 800,
  keuken: 1200,
  badkamer: 1000,
  kantoor: 3600, // 60×60 paneel
  winkel: 3600,
  werkplaats: 5000, // TL-vervanger 1500mm
  magazijn: 10000, // high-bay klein
  garage: 4000,
  buiten: 3000, // straler
};

const FAQS = [
  {
    q: "Hoe wordt het benodigde wattage berekend?",
    a: "Wij rekenen met: oppervlakte (m²) × aanbevolen lux × hoogtefactor = benodigde lumen. Dat delen we door de LED-efficiency (lm/W) om het wattage te bepalen. De aanbevolen lux-waarden komen uit de Europese verlichtingsnorm NEN-EN 12464-1.",
  },
  {
    q: "Waarom telt de plafondhoogte mee?",
    a: "Lichtsterkte op het werkvlak neemt af met het kwadraat van de afstand. Bij een hoger plafond moet de lamp dus meer lumen leveren om dezelfde lux op de vloer te halen. Wij gebruiken een hoogtefactor van 1.0 (≤2,6 m) tot 3.2 (>10 m).",
  },
  {
    q: "Wat is het verschil tussen lumen, lux en watt?",
    a: "Lumen (lm) is de totale lichtopbrengst van een lamp. Lux (lx) is hoeveel lumen er per m² op een oppervlak valt — dus de praktische helderheid. Watt (W) is het opgenomen vermogen; bij LED telt vooral de lm/W-verhouding (efficacy).",
  },
  {
    q: "Welke kleurtemperatuur past bij welke ruimte?",
    a: "Warm wit (2700–3000K) past in woonkamer en slaapkamer. Neutraal wit (4000K) is ideaal voor keuken, kantoor en badkamer. Daglicht wit (5000–6500K) gebruik je in werkplaats, magazijn en buiten — daar wil je maximaal kleur- en contrastonderscheid.",
  },
  {
    q: "Hoeveel bespaar ik t.o.v. halogeen of TL?",
    a: "LED verbruikt circa 80–85% minder dan een gloei- of halogeenlamp en 40–55% minder dan klassieke TL. Een 36W TL-buis vervang je gemiddeld door een 18W LED-buis met gelijke of hogere lichtopbrengst.",
  },
  {
    q: "Heb ik een dimbare LED nodig?",
    a: "Dimbaarheid is handig in woonkamer, slaapkamer en restaurant — overal waar je sfeer regelt. Combineer dimbare LED altijd met een LED-geschikte (fase-aansnijdings)dimmer, anders krijg je flikkering of brom.",
  },
  {
    q: "Wat is een goede lm/W-waarde voor LED?",
    a: "Een goede consumenten-LED haalt 80–100 lm/W. Professionele LED-panelen en TL-vervangers zitten op 120–140 lm/W. High-bay armaturen voor magazijnen halen tegenwoordig 150–180 lm/W.",
  },
  {
    q: "Geldt deze berekening ook voor buitenverlichting?",
    a: "Ja, maar reken bij buitenverlichting altijd met een stralingshoek en kies armaturen met IP65 of hoger. Voor terreinverlichting is gerichte LED-stralers vaak efficiënter dan algemene verlichting met hoge lumen.",
  },
];

function LedCalculatorPage() {
  const [area, setArea] = useState(25);
  const [height, setHeight] = useState(2.6);
  const [room, setRoom] = useState<string>("woonkamer");
  const [efficacy, setEfficacy] = useState<EfficacyKey>("standaard");
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const { lux, totalLumen, totalWatt, wattPerM2, armatures, hf } = useMemo(() => {
    const r = ROOM_TYPES.find((x) => x.key === room) ?? ROOM_TYPES[0];
    const hf = heightFactor(height);
    const totalLumen = Math.round(area * r.lux * hf);
    const lmW = EFFICACY[efficacy];
    const totalWatt = Math.round(totalLumen / lmW);
    const wattPerM2 = area > 0 ? totalWatt / area : 0;
    const lumenPerArmatuur =
      ARMATUUR_LUMEN[room as keyof typeof ARMATUUR_LUMEN] ?? 1000;
    const armatures = Math.max(1, Math.ceil(totalLumen / lumenPerArmatuur));
    return {
      lux: r.lux,
      totalLumen,
      totalWatt,
      wattPerM2,
      armatures,
      hf,
    };
  }, [area, height, room, efficacy]);

  return (
    <>
      <Header />
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">

        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
          LED Wattage Calculator: bereken het juiste vermogen per ruimte
        </h1>
        <p className="mt-4 max-w-3xl text-base text-text-secondary sm:text-lg">
          Hoeveel <strong>watt LED-verlichting</strong> heb je nodig voor jouw
          ruimte? Vul de oppervlakte, plafondhoogte en het type ruimte in. Wij
          berekenen direct het benodigde aantal lumen, het wattage en het aantal
          armaturen — gebaseerd op de Europese verlichtingsnorm NEN-EN 12464-1.
        </p>

        {/* Calculator */}
        <section className="mt-10 grid gap-6 lg:grid-cols-[1.1fr_1fr]">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
            <div className="mb-6 flex items-center gap-2 text-orange">
              <Calculator className="size-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">
                Calculator
              </span>
            </div>

            <div className="space-y-6">
              <div>
                <label className="text-sm font-semibold text-foreground">
                  Type ruimte
                </label>
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {ROOM_TYPES.map((r) => (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => setRoom(r.key)}
                      className={`rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                        room === r.key
                          ? "border-orange bg-orange text-white"
                          : "border-border bg-background text-foreground hover:border-orange"
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-text-secondary">
                  Aanbevolen: {lux} lux —{" "}
                  {ROOM_TYPES.find((x) => x.key === room)?.hint}
                </p>
              </div>

              <div>
                <label className="flex items-center justify-between text-sm font-semibold text-foreground">
                  Oppervlakte (m²)
                  <span className="rounded-md bg-muted px-2 py-0.5 text-base font-bold text-foreground">
                    {area} m²
                  </span>
                </label>
                <input
                  type="range"
                  min={2}
                  max={500}
                  value={area}
                  onChange={(e) => setArea(Number(e.target.value))}
                  className="mt-3 w-full accent-orange"
                />
                <div className="mt-1 flex justify-between text-xs text-text-secondary">
                  <span>2 m²</span>
                  <span>500 m²</span>
                </div>
              </div>

              <div>
                <label className="flex items-center justify-between text-sm font-semibold text-foreground">
                  Plafondhoogte
                  <span className="rounded-md bg-muted px-2 py-0.5 text-base font-bold text-foreground">
                    {height.toFixed(1)} m
                  </span>
                </label>
                <input
                  type="range"
                  min={2}
                  max={12}
                  step={0.1}
                  value={height}
                  onChange={(e) => setHeight(Number(e.target.value))}
                  className="mt-3 w-full accent-orange"
                />
                <div className="mt-1 flex justify-between text-xs text-text-secondary">
                  <span>2 m</span>
                  <span>woning ±2,6 m</span>
                  <span>12 m</span>
                </div>
                <p className="mt-2 text-xs text-text-secondary">
                  Hoogtefactor: ×{hf.toFixed(1)} (compenseert lichtverlies bij
                  hogere plafonds).
                </p>
              </div>

              <div>
                <label className="text-sm font-semibold text-foreground">
                  Kwaliteit LED (lm/W)
                </label>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {(Object.keys(EFFICACY) as EfficacyKey[]).map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setEfficacy(k)}
                      className={`rounded-md border px-3 py-2 text-xs font-semibold transition-colors ${
                        efficacy === k
                          ? "border-orange bg-orange text-white"
                          : "border-border bg-background text-foreground hover:border-orange"
                      }`}
                    >
                      <span className="block capitalize">{k}</span>
                      <span className="block text-[10px] opacity-80">
                        {EFFICACY[k]} lm/W
                      </span>
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-text-secondary">
                  Standaard = retail A-merk · Hoog = LED-paneel/TL-vervanger ·
                  Premium = high-bay armatuur.
                </p>
              </div>
            </div>
          </div>

          {/* Resultaat */}
          <div className="rounded-2xl border border-orange/30 bg-gradient-to-br from-orange/5 to-transparent p-6 shadow-sm sm:p-8">
            <div className="mb-4 flex items-center gap-2 text-orange">
              <Lightbulb className="size-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">
                Jouw verlichtingsadvies
              </span>
            </div>

            <p className="text-sm text-text-secondary">Benodigd vermogen</p>
            <p className="mt-1 text-4xl font-bold tracking-tight text-foreground">
              {totalWatt} W
            </p>
            <p className="mt-1 text-xs text-text-secondary">
              ≈ {wattPerM2.toFixed(1)} W/m² · {EFFICACY[efficacy]} lm/W
            </p>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-border bg-background p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
                  Totaal lumen
                </p>
                <p className="mt-1 text-xl font-bold text-foreground">
                  {totalLumen.toLocaleString("nl-NL")} lm
                </p>
              </div>
              <div className="rounded-xl border border-border bg-background p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
                  Aantal armaturen
                </p>
                <p className="mt-1 text-xl font-bold text-foreground">
                  ± {armatures}
                </p>
              </div>
            </div>

            <p className="mt-3 text-xs text-text-secondary">
              Op basis van een gemiddelde lichtopbrengst per armatuur voor deze
              ruimte. Voor exacte spreiding adviseren wij een lichtplan.
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/c/$category"
                params={{ category: "verlichting" }}
                className="inline-flex items-center gap-2 rounded-md bg-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange/90"
              >
                Naar LED verlichting
                <ArrowRight className="size-4" />
              </Link>
              <Link
                to="/"
                hash="contact"
                className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground hover:border-orange hover:text-orange"
              >
                Lichtplan aanvragen
              </Link>
            </div>
          </div>
        </section>

        {/* Lux-tabel */}
        <section className="mt-12">
          <h2 className="text-xl font-bold tracking-tight">
            Aanbevolen lux per ruimte (NEN-EN 12464-1)
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            Richtwaarden voor verlichtingssterkte op het werk- of leefvlak.
          </p>
          <div className="mt-4 overflow-hidden rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-text-secondary">
                <tr>
                  <th className="px-4 py-3">Ruimte</th>
                  <th className="px-4 py-3">Aanbevolen lux</th>
                  <th className="px-4 py-3">Typisch gebruik</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {ROOM_TYPES.map((r) => (
                  <tr
                    key={r.key}
                    className={r.key === room ? "bg-orange/5" : ""}
                  >
                    <td className="px-4 py-3 font-semibold text-foreground">
                      {r.label}
                    </td>
                    <td className="px-4 py-3">{r.lux} lx</td>
                    <td className="px-4 py-3 text-text-secondary">{r.hint}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Voordelen & Tips */}
        <section className="mt-12 grid gap-8 lg:grid-cols-2">
          <div>
            <h2 className="text-xl font-bold tracking-tight">
              Waarom LED kiezen?
            </h2>
            <ul className="mt-3 space-y-2">
              {[
                "Tot 85% lager energieverbruik dan halogeen/gloeilamp",
                "Levensduur 15.000–50.000 branduren",
                "Direct 100% lichtopbrengst, geen opwarmtijd",
                "Geen UV/IR-straling — veilig voor producten en mensen",
                "Beschikbaar in IP20 (binnen) tot IP67 (buiten/industrie)",
              ].map((b) => (
                <li
                  key={b}
                  className="flex gap-2 text-text-secondary before:mt-2 before:size-1.5 before:shrink-0 before:rounded-full before:bg-orange"
                >
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-xl font-bold tracking-tight">
              Praktische tips
            </h2>
            <ul className="mt-3 space-y-2">
              {[
                "Reken altijd 10–15% extra lumen voor lichtverlies (vuil, ouderdom)",
                "Combineer algemene verlichting met taakverlichting (keukenblad, bureau)",
                "Kies hoge CRI (≥80) voor natuurlijke kleurweergave",
                "Werkplaats/magazijn: kies armaturen met brede stralingshoek (≥120°)",
                "Gebruik bewegingssensoren in gangen en garage voor extra besparing",
              ].map((b) => (
                <li
                  key={b}
                  className="flex gap-2 text-text-secondary before:mt-2 before:size-1.5 before:shrink-0 before:rounded-full before:bg-orange"
                >
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-12">
          <h2 className="text-2xl font-bold tracking-tight">
            Veelgestelde vragen over LED-verlichting
          </h2>
          <div className="mt-4 divide-y divide-border rounded-xl border border-border bg-card">
            {FAQS.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={f.q} className="p-5">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="flex w-full items-center justify-between text-left font-semibold text-foreground"
                  >
                    {f.q}
                    <span
                      className={`ml-3 text-orange transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    >
                      ▼
                    </span>
                  </button>
                  {isOpen && (
                    <p className="mt-2 text-text-secondary">{f.a}</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <section className="mt-12 rounded-2xl bg-card p-6 ring-1 ring-border sm:p-8">
          <h2 className="text-xl font-bold tracking-tight">
            Hulp nodig bij je verlichtingsplan?
          </h2>
          <p className="mt-2 text-text-secondary">
            Onze adviseurs maken graag een lichtplan op maat — inclusief
            armatuurkeuze, plaatsing en eventueel een DIALux-berekening voor
            grotere projecten.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/c/$category"
              params={{ category: "verlichting" }}
              className="inline-flex items-center rounded-md bg-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange/90"
            >
              Bekijk alle LED verlichting
            </Link>
            <Link
              to="/"
              hash="contact"
              className="inline-flex items-center rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:border-orange hover:text-orange"
            >
              Neem contact op
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
